import { keyBy } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { FragmentType, getFragmentData, gql } from "__generated__/gql";
import { AccessPlanType } from "__generated__/gql/graphql";

const accessPlanFragment = gql(/* GraphQL */ `
  fragment SignupPlanSelectionStateAccessPlan on AccessPlan {
    id
    name
    planType
    slug
    stripeProductId
  }
`);

type Params = {
  accessPlans: FragmentType<typeof accessPlanFragment>[] | null | undefined;
  brandAccessPlanId: UUID | null | undefined;
  selectedPlanSlug?: string;
};

export default function useSignupPlanSelectionState({
  accessPlans: accessPlanFragmentRefs,
  brandAccessPlanId,
  selectedPlanSlug,
}: Params) {
  const accessPlans = getFragmentData(
    accessPlanFragment,
    accessPlanFragmentRefs,
  );

  const selectableAccessPlans = useMemo(
    () =>
      accessPlans?.filter(
        (accessPlan) =>
          accessPlan.planType === AccessPlanType.FREE ||
          !!accessPlan.stripeProductId,
      ) ?? [],
    [accessPlans],
  );

  const selectableAccessPlansById = useMemo(
    () => keyBy(selectableAccessPlans, "id"),
    [selectableAccessPlans],
  );

  const initialSelectedAccessPlanId = useMemo(() => {
    if (selectedPlanSlug) {
      const selectableAccessPlansBySlug = keyBy(selectableAccessPlans, "slug");
      const selectedPlan = selectableAccessPlansBySlug[selectedPlanSlug];

      if (selectedPlan) return selectedPlan.id;
    }

    if (brandAccessPlanId && selectableAccessPlansById[brandAccessPlanId]) {
      return brandAccessPlanId;
    }

    return selectableAccessPlans[0]?.id;
  }, [
    brandAccessPlanId,
    selectableAccessPlans,
    selectableAccessPlansById,
    selectedPlanSlug,
  ]);

  const [selectedAccessPlanId, setSelectedAccessPlanId] = useState(
    initialSelectedAccessPlanId,
  );

  useEffect(() => {
    if (!selectedAccessPlanId && initialSelectedAccessPlanId) {
      setSelectedAccessPlanId(initialSelectedAccessPlanId);
    }
  }, [initialSelectedAccessPlanId, selectedAccessPlanId]);

  const selectedAccessPlan = selectedAccessPlanId
    ? selectableAccessPlansById[selectedAccessPlanId]
    : undefined;

  return {
    selectedAccessPlan,
    setSelectedAccessPlanId,
  };
}
